import debug from 'debug';
import { serverLogger } from '../libs/datadog-logger/loggers/serverLogger';

export const AUTH_DATA_KEY = 'authorizationData';
export const AUTH_DATA_KEY_BACKUP = 'authorizationDataBackup';

const debugStorage = debug('auth:storage');

class TokenStorage {
    //reload keep sessionStorage

    static save(authData) {
        debugStorage('Saving auth data to storage', {
            hasToken: !!authData?.token,
            tokenLength: authData?.token?.length
        });
        // ? STEP 7: Add timestamp for race condition detection
        const timestamp = Date.now();
        const dataWithTimestamp = {
            ...authData,
            timestamp,
            version: 1, // For future schema migrations
        };

        // Read current timestamp before write
        const current = this.get();
        let currentTimestamp = 0;
        try {
            if (current) {
                const parsed = JSON.parse(current);
                currentTimestamp = parsed.timestamp || 0;
            }
        } catch {
            // Ignore parse errors for old format
        }

        // Skip write if a newer token already exists
        if (currentTimestamp && currentTimestamp > timestamp) {
            console.warn('[TokenStorage] Skipping write - newer tokens already exist', {
                currentTimestamp,
                attemptedTimestamp: timestamp,
                age: timestamp - currentTimestamp,
            });

            serverLogger.warn('auth.storage_race_prevented', {
                currentTimestamp,
                attemptedTimestamp: timestamp,
                winner: 'existing-tokens',
            });

            return;
        }

        // Write to both storages
        const serialized = JSON.stringify(dataWithTimestamp);
        localStorage.setItem(AUTH_DATA_KEY, serialized);
        sessionStorage.setItem(AUTH_DATA_KEY, serialized);

        // ? Verify write succeeded (detect race condition)
        try {
            const written = this.get();
            if (written) {
                const parsedWritten = JSON.parse(written);
                if (parsedWritten.timestamp && parsedWritten.timestamp !== timestamp) {
                    console.warn(
                        '[TokenStorage] Race detected - another tab wrote during our write',
                        {
                            ourTimestamp: timestamp,
                            theirTimestamp: parsedWritten.timestamp,
                        }
                    );

                    // ? Emit custom event for monitoring
                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('auth:storage-race', {
                            detail: {
                                ourTimestamp: timestamp,
                                theirTimestamp: parsedWritten.timestamp,
                                winner: parsedWritten.timestamp > timestamp ? 'other-tab' : 'this-tab',
                            },
                        }));

                        // Track in Datadog
                        serverLogger.warn('auth.storage_race_detected', {
                            ourTimestamp: timestamp,
                            theirTimestamp: parsedWritten.timestamp,
                            winner: parsedWritten.timestamp > timestamp ? 'other-tab' : 'this-tab',
                        });
                    }

                    // ? Keep the newest tokens (highest timestamp wins)
                    if (parsedWritten.timestamp < timestamp) {
                        console.info('[TokenStorage] Re-writing with newer tokens');
                        localStorage.setItem(AUTH_DATA_KEY, serialized);
                        sessionStorage.setItem(AUTH_DATA_KEY, serialized);
                    } else {
                        console.info('[TokenStorage] Keeping other tab\'s newer tokens');
                    }
                }
            }
        } catch (error) {
            console.error('[TokenStorage] Failed to verify write:', error);
        }
    }
    static get() {
        const data = localStorage.getItem(AUTH_DATA_KEY) || sessionStorage.getItem(AUTH_DATA_KEY);
        debugStorage('Getting auth data from storage', {
            hasData: !!data,
            dataLength: data?.length
        });
        return data;
    }
    static clear() {
        const stack = new Error().stack;
        const caller = stack?.split('\n')[2]?.trim() || 'unknown';
        const fullStack = stack?.split('\n').slice(1, 6).map(line => line.trim()).join(' > ') || 'unknown';

        debugStorage('?? CLEARING AUTH DATA', {
            caller,
            fullStack,
            timestamp: new Date().toISOString()
        });
        console.warn('[TokenStorage] CLEAR CALLED', {
            caller,
            fullStack,
            timestamp: new Date().toISOString()
        });

        localStorage.removeItem(AUTH_DATA_KEY);
        sessionStorage.removeItem(AUTH_DATA_KEY);
    }

    // Alias for clear() - used by session.js and other modules
    static remove() {
        const stack = new Error().stack;
        const caller = stack?.split('\n')[2]?.trim() || 'unknown';
        const fullStack = stack?.split('\n').slice(1, 10).map(line => line.trim()).join('\n        ') || 'unknown';

        debugStorage('?? REMOVE CALLED (will delegate to clear)', {
            caller,
            fullStack,
            timestamp: new Date().toISOString()
        });
        console.warn('[TokenStorage] REMOVE CALLED - Full Stack:', {
            caller,
            fullStack,
            timestamp: new Date().toISOString()
        });

        this.clear();
    }

    static backup(){
        const data = this.get();
        localStorage.setItem(AUTH_DATA_KEY_BACKUP, data);
    }
    static restoreFromBackup(){
        
        const data = localStorage.getItem(AUTH_DATA_KEY_BACKUP);
        localStorage.setItem(AUTH_DATA_KEY, data);

    }

    
    static tryRestore() {
        const localAuthData = localStorage.getItem(AUTH_DATA_KEY);
        if (!localAuthData) {
            const authorizationData = sessionStorage.getItem(AUTH_DATA_KEY);
            if (authorizationData) {
                const authObj = JSON.parse(authorizationData);
                if (authObj && authObj.token) {
                    localStorage.setItem(AUTH_DATA_KEY, authorizationData);
                }
            }
        }
    }

}

export default TokenStorage;
